import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion"; // Updated import
import { X, Rocket, Sparkles, Hammer, Zap } from "lucide-react";
import { useComingSoon } from "../utils/useComingSoon";

export default function ComingSoonModal() {
  const {
    isComingSoonOpen,
    hideComingSoon,
    comingSoonTitle,
    comingSoonMessage,
  } = useComingSoon();

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isComingSoonOpen) {
        hideComingSoon();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isComingSoonOpen, hideComingSoon]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isComingSoonOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isComingSoonOpen]);

  return (
    <AnimatePresence>
      {isComingSoonOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={hideComingSoon}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md bg-white dark:bg-[#1A1A1A] rounded-3xl shadow-2xl overflow-hidden z-10"
          >
            {/* Close Button */}
            <button
              onClick={hideComingSoon}
              className="absolute top-4 right-4 p-2 hover:bg-[#F3F4F6] dark:hover:bg-[#262626] rounded-full transition-colors z-10"
            >
              <X size={20} className="text-[#6B7280] dark:text-[#A0A0A0]" />
            </button>

            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="absolute -top-20 -right-20 w-40 h-40 bg-[#62397f]/10 rounded-full blur-3xl"
              />
              <motion.div
                animate={{
                  rotate: -360,
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 15,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#EC4899]/10 rounded-full blur-3xl"
              />
            </div>

            {/* Content */}
            <div className="relative p-8 text-center">
              {/* Icon Animation */}
              <div className="relative mb-6 flex justify-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.1 }}
                  className="relative"
                >
                  {/* Main Icon Container */}
                  <div className="w-24 h-24 bg-gradient-to-br from-[#62397f] to-[#EC4899] rounded-3xl flex items-center justify-center shadow-xl shadow-[#62397f]/30 rotate-12">
                    <Rocket size={48} className="text-white -rotate-12" />
                  </div>

                  {/* Floating Sparkles */}
                  <motion.div
                    animate={{
                      y: [-5, 5, -5],
                      rotate: [0, 10, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="absolute -top-2 -right-2"
                  >
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                      <Sparkles size={16} className="text-white" />
                    </div>
                  </motion.div>

                  <motion.div
                    animate={{
                      y: [5, -5, 5],
                      rotate: [0, -10, 0],
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 0.5,
                    }}
                    className="absolute -bottom-1 -left-2"
                  >
                    <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center">
                      <Zap size={12} className="text-white" />
                    </div>
                  </motion.div>
                </motion.div>
              </div>

              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-3xl font-bold text-[#111111] dark:text-white mb-3 font-poppins"
              >
                {comingSoonTitle || "Coming Soon!"}
              </motion.h2>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-[#6B7280] dark:text-[#A0A0A0] mb-6 font-inter leading-relaxed"
              >
                {comingSoonMessage ||
                  "We're working hard to bring you something amazing. This feature is currently under development and will be available soon!"}
              </motion.p>

              {/* Feature Badges */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap gap-2 justify-center mb-8"
              >
                <span className="px-4 py-2 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] text-sm font-semibold rounded-full flex items-center gap-2">
                  <Hammer size={14} />
                  In Progress
                </span>
                <span className="px-4 py-2 bg-[#FEF3C7] dark:bg-[#451A03] text-[#F59E0B] dark:text-[#FCD34D] text-sm font-semibold rounded-full flex items-center gap-2">
                  <Sparkles size={14} />
                  Exciting
                </span>
              </motion.div>

              {/* CTA Button */}
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                onClick={hideComingSoon}
                className="w-full px-8 py-4 bg-gradient-to-r from-[#62397f] to-[#EC4899] hover:from-[#7C3AED] hover:to-[#DB2777] text-white font-bold rounded-xl transition-all duration-200 shadow-lg shadow-[#62397f]/30 active:scale-95"
              >
                Got it!
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
